import { Layer } from '@deck.gl/core';
import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { SnLayer, SnLayerSetting } from '@lib/api/entities';
import {
  FsMapLayersServiceProvider,
  IFsMap,
  IMarkerProperties,
  IFeatureDetailsPopoutComponent,
} from '@lib/geo/core';

/**
 * Facade for map layers that delegates to an injected provider.
 */
@Injectable()
export class MapLayersFacadeService<
  TOverlay,
  TMarker,
  TMap extends IFsMap,
> extends FsMapLayersServiceProvider<TOverlay, TMarker, TMap> {
  constructor(private readonly provider: FsMapLayersServiceProvider<TOverlay, TMarker, TMap>) {
    super();
  }

  // Re-implemented getters
  public get layerSettings(): any | SnLayerSetting {
    return this.provider.layerSettings;
  }

  public get zoomToExtent$(): Observable<SnLayer> {
    return this.provider.zoomToExtent$;
  }

  public get avlMarkers(): any[] {
    return this.provider.avlMarkers;
  }

  public get busModemMarkers(): any[] {
    return this.provider.busModemMarkers;
  }

  public get skydioMarker(): any[] {
    return this.provider.skydioMarker;
  }

  public get layerMarkers(): TMarker[] {
    return this.provider.layerMarkers;
  }

  public get activePopouts(): MatDialogRef<IFeatureDetailsPopoutComponent>[] {
    return this.provider.activePopouts;
  }

  public get activePopoutCount(): number {
    return this.provider.activePopoutCount;
  }

  public get enabledLayers(): Map<string, SnLayer> {
    return this.provider.enabledLayers;
  }

  // Re-implemented properties
  public get isAvailable(): boolean {
    return this.provider.isAvailable;
  }
  public set isAvailable(value: boolean) {
    this.provider.isAvailable = value;
  }

  public get registryCameras(): any[] {
    return this.provider.registryCameras;
  }
  public set registryCameras(value: any[]) {
    this.provider.registryCameras = value;
  }

  public get defaultAvlLayer(): SnLayer {
    return this.provider.defaultAvlLayer;
  }
  public set defaultAvlLayer(value: SnLayer) {
    this.provider.defaultAvlLayer = value;
  }

  public get activeAvlLayer(): SnLayer {
    return this.provider.activeAvlLayer;
  }
  public set activeAvlLayer(value: SnLayer) {
    this.provider.activeAvlLayer = value;
  }

  public get minimumZoomLayers(): any[] {
    return this.provider.minimumZoomLayers;
  }
  public set minimumZoomLayers(value: any[]) {
    this.provider.minimumZoomLayers = value;
  }

  public get deckGLOverlay(): TOverlay[] {
    return this.provider.deckGLOverlay;
  }
  public set deckGLOverlay(value: TOverlay[]) {
    this.provider.deckGLOverlay = value;
  }

  public get deckGlLayers(): Layer[] {
    return this.provider.deckGlLayers;
  }
  public set deckGlLayers(value: Layer[]) {
    this.provider.deckGlLayers = value;
  }

  public get markers(): any {
    return this.provider.markers;
  }
  public set markers(value: any) {
    this.provider.markers = value;
  }

  public get followMarker(): any {
    return this.provider.followMarker;
  }
  public set followMarker(value: any) {
    this.provider.followMarker = value;
  }

  // Re-implemented methods
  public onMapLoaded(map: TMap): void {
    return this.provider.onMapLoaded(map);
  }

  public isEnabled(id: string): boolean {
    return this.provider.isEnabled(id);
  }

  public isLoading(id: string): boolean {
    return this.provider.isLoading(id);
  }

  public toggle(layer: SnLayer): Observable<boolean> {
    return this.provider.toggle(layer);
  }

  public openPopout(layer: SnLayer, marker: any, fields: string[]): void {
    return this.provider.openPopout(layer, marker, fields);
  }

  public addPolyline(start: any, end: any): void {
    return this.provider.addPolyline(start, end);
  }

  public removePolylines(): void {
    return this.provider.removePolylines();
  }

  public zoomToExtent(layer: SnLayer): void {
    return this.provider.zoomToExtent(layer);
  }

  public openAxonOfficerListDialog(layer: SnLayer): void {
    return this.provider.openAxonOfficerListDialog(layer);
  }

  public followOfficerMarker(layer: SnLayer, markerProperties: IMarkerProperties): void {
    return this.provider.followOfficerMarker(layer, markerProperties);
  }

  public colorCheck(layer: SnLayer): boolean {
    return this.provider.colorCheck(layer);
  }

  public getColor(layer: SnLayer, num: string): string {
    return this.provider.getColor(layer, num);
  }

  public trackMarkerClick(markerData: any, layer?: SnLayer): void {
    return this.provider.trackMarkerClick(markerData, layer);
  }
}
